import { useEffect, useState, useCallback, Fragment } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, UserPlus, KeyRound, MapPin, Check, X, Pencil } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/dashboard/Header";

const SCOPED_ROLES = ["regional", "community"];
const isScoped = (role) => SCOPED_ROLES.includes(role);

function LocationPicker({ allLocations, selected, allLocationsFlag, onToggleAll, onToggle, testidPrefix }) {
  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-location-picker`}>
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input type="checkbox" checked={allLocationsFlag} onChange={onToggleAll}
          data-testid={`${testidPrefix}-all-locations`}
          className="h-4 w-4 accent-brand-primary" />
        <span className="text-sm text-brand-ink">Access all locations</span>
      </label>
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-1.5 ${allLocationsFlag ? "opacity-40 pointer-events-none" : ""}`}>
        {allLocations.map((loc) => (
          <label key={loc} className="flex items-center gap-2 cursor-pointer select-none text-sm">
            <input type="checkbox" checked={selected.includes(loc)} onChange={() => onToggle(loc)}
              data-testid={`${testidPrefix}-loc-${loc}`}
              className="h-3.5 w-3.5 accent-brand-primary" />
            <span className="text-brand-muted">{loc}</span>
          </label>
        ))}
      </div>
      {!allLocationsFlag && selected.length === 0 && (
        <p className="text-[11px] text-brand-neg" data-testid={`${testidPrefix}-empty-warn`}>
          No locations selected — this user will see no data until you assign at least one.
        </p>
      )}
    </div>
  );
}

export default function Investors() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "", role: "investor", locations: [], all_locations: false });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ role: "investor", locations: [], all_locations: false });

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data.users);
  }, []);
  const loadLocations = useCallback(async () => {
    try { const { data } = await api.get("/admin/locations"); setAllLocations(data.locations || []); }
    catch { setAllLocations([]); }
  }, []);
  useEffect(() => { if (user?.role === "admin") { load(); loadLocations(); } }, [user, load, loadLocations]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const invite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const name = `${form.firstName} ${form.lastName}`.trim();
      await api.post("/admin/users", {
        name, email: form.email, password: form.password, role: form.role,
        locations: isScoped(form.role) ? form.locations : [],
        all_locations: isScoped(form.role) ? form.all_locations : false,
      });
      toast.success("Account created", { description: `${form.email} can now sign in.` });
      setForm({ firstName: "", lastName: "", email: "", password: "", role: "investor", locations: [], all_locations: false });
      await load();
    } catch (err) {
      toast.error("Could not create account", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? They will no longer be able to sign in.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Removed ${u.email}`);
      await load();
    } catch (err) {
      toast.error("Could not remove", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const resetPw = async (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 6 chars):`);
    if (!pw) return;
    try {
      await api.patch(`/admin/users/${u.id}/password`, { password: pw });
      toast.success("Password updated");
    } catch (err) {
      toast.error("Could not update", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditDraft({ role: u.role, locations: u.locations || [], all_locations: !!u.all_locations });
  };
  const saveEdit = async (u) => {
    const scoped = isScoped(editDraft.role);
    try {
      await api.patch(`/admin/users/${u.id}`, {
        role: editDraft.role,
        locations: scoped ? editDraft.locations : [],
        all_locations: scoped ? editDraft.all_locations : false,
      });
      toast.success("User updated", { description: u.email });
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error("Could not update user", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const summarizeLoc = (u) => {
    if (u.all_locations) return "All locations";
    if (!u.locations || u.locations.length === 0) return "No locations assigned";
    return u.locations.join(", ");
  };

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />

      <main className="max-w-5xl mx-auto px-6 lg:px-12 py-10">
        <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">Administration</p>
        <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight text-brand-ink mt-2">Users</h1>
        <p className="text-sm text-brand-muted mt-1 mb-8">Create and manage logins. Regional & Community users only see the locations you assign them.</p>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Invite form */}
          <div className="bg-brand-surface border border-brand-border p-6 lg:col-span-1 h-fit" data-testid="invite-panel">
            <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary flex items-center gap-1.5"><UserPlus className="h-3.5 w-3.5" /> Add User</p>
            <form onSubmit={invite} className="mt-5 space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">First Name</label>
                  <input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    data-testid="invite-first-name" className="w-full bg-transparent border-0 border-b border-brand-borderStrong py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-primary transition-colors" placeholder="Jane" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">Last Name</label>
                  <input required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    data-testid="invite-last-name" className="w-full bg-transparent border-0 border-b border-brand-borderStrong py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-primary transition-colors" placeholder="Investor" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">Email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="invite-email" className="w-full bg-transparent border-0 border-b border-brand-borderStrong py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-primary transition-colors" placeholder="jane@fund.com" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">Role</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="invite-role" className="w-full bg-transparent border-0 border-b border-brand-borderStrong py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-primary transition-colors">
                  <option value="investor">Investor — read-only: financials, reports & sales</option>
                  <option value="community">Community — edit sales, CRM & proposals (their locations)</option>
                  <option value="regional">Regional — manage their locations (view financials + edit sales/CRM/proposals)</option>
                  <option value="finance">Finance / AP — accounts payable only</option>
                  <option value="admin">Admin — full access</option>
                </select>
              </div>
              {isScoped(form.role) && (
                <div className="pt-1">
                  <label className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary flex items-center gap-1.5 mb-2"><MapPin className="h-3 w-3" /> Assigned Locations</label>
                  <LocationPicker
                    testidPrefix="invite"
                    allLocations={allLocations}
                    selected={form.locations}
                    allLocationsFlag={form.all_locations}
                    onToggleAll={() => setForm((f) => ({ ...f, all_locations: !f.all_locations }))}
                    onToggle={(loc) => setForm((f) => ({ ...f, locations: f.locations.includes(loc) ? f.locations.filter((l) => l !== loc) : [...f.locations, loc] }))}
                  />
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary">Temporary Password</label>
                <input required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="invite-password" className="w-full bg-transparent border-0 border-b border-brand-borderStrong py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-primary transition-colors" placeholder="min 6 characters" />
              </div>
              <button type="submit" disabled={saving} data-testid="invite-submit"
                className="w-full bg-brand-primary text-brand-page py-3 text-sm tracking-wide hover:bg-brand-primaryHover active:scale-[0.98] transition-all duration-200 disabled:opacity-60">
                {saving ? "Creating\u2026" : "Create Account"}
              </button>
            </form>
          </div>

          {/* Users list */}
          <div className="bg-brand-surface border border-brand-border lg:col-span-2" data-testid="users-table">
            <div className="px-6 py-4 border-b border-brand-border">
              <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">All Accounts ({users.length})</p>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {users.map((u) => (
                  <Fragment key={u.id}>
                    <tr data-testid={`user-row-${u.email}`} className="border-b border-brand-border last:border-0">
                      <td className="px-6 py-4 align-top">
                        <p className="text-brand-ink">{u.name || "\u2014"}</p>
                        <p className="text-brand-tertiary text-xs">{u.email}</p>
                        {isScoped(u.role) && (
                          <p className="text-[11px] text-brand-muted mt-1 flex items-center gap-1" data-testid={`user-locs-${u.email}`}>
                            <MapPin className="h-3 w-3 text-brand-tertiary" /> {summarizeLoc(u)}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full ${u.role === "admin" ? "bg-brand-ink text-brand-page" : "bg-brand-hover text-brand-muted"}`}>{u.role}</span>
                      </td>
                      <td className="px-6 py-4 text-right align-top">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => (editingId === u.id ? setEditingId(null) : startEdit(u))} data-testid={`edit-user-${u.email}`} title="Edit role & locations"
                            className="p-1.5 text-brand-tertiary hover:text-brand-primary transition-colors"><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => resetPw(u)} data-testid={`reset-pw-${u.email}`} title="Reset password"
                            className="p-1.5 text-brand-tertiary hover:text-brand-ink transition-colors"><KeyRound className="h-4 w-4" /></button>
                          <button onClick={() => remove(u)} data-testid={`delete-user-${u.email}`} title="Remove"
                            className="p-1.5 text-brand-tertiary hover:text-brand-neg transition-colors"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                    {editingId === u.id && (
                      <tr key={`${u.id}-edit`} className="border-b border-brand-border bg-brand-hover/40">
                        <td colSpan={3} className="px-6 py-5" data-testid={`user-editor-${u.email}`}>
                          <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-2">Role — {u.email}</p>
                          <select value={editDraft.role} onChange={(e) => setEditDraft((d) => ({ ...d, role: e.target.value }))}
                            data-testid={`edit-role-${u.email}`}
                            className="w-full bg-transparent border-0 border-b border-brand-borderStrong py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-primary transition-colors mb-4">
                            <option value="investor">Investor — read-only: financials, reports &amp; sales</option>
                            <option value="community">Community — edit sales, CRM &amp; proposals (their locations)</option>
                            <option value="regional">Regional — manage their locations (view financials + edit sales/CRM/proposals)</option>
                            <option value="finance">Finance / AP — accounts payable only</option>
                  <option value="admin">Admin — full access</option>
                          </select>
                          {isScoped(editDraft.role) && (
                            <>
                              <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-3">Assigned Locations</p>
                              <LocationPicker
                                testidPrefix={`edit-${u.email}`}
                                allLocations={allLocations}
                                selected={editDraft.locations}
                                allLocationsFlag={editDraft.all_locations}
                                onToggleAll={() => setEditDraft((d) => ({ ...d, all_locations: !d.all_locations }))}
                                onToggle={(loc) => setEditDraft((d) => ({ ...d, locations: d.locations.includes(loc) ? d.locations.filter((l) => l !== loc) : [...d.locations, loc] }))}
                              />
                            </>
                          )}
                          <div className="flex items-center gap-2 mt-4">
                            <button onClick={() => saveEdit(u)} data-testid={`save-user-${u.email}`}
                              className="inline-flex items-center gap-1.5 bg-brand-primary text-brand-page px-4 py-2 text-xs tracking-wide hover:bg-brand-primaryHover transition-colors"><Check className="h-3.5 w-3.5" /> Save</button>
                            <button onClick={() => setEditingId(null)} data-testid={`cancel-user-${u.email}`}
                              className="inline-flex items-center gap-1.5 border border-brand-borderStrong text-brand-muted px-4 py-2 text-xs tracking-wide hover:text-brand-ink transition-colors"><X className="h-3.5 w-3.5" /> Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
